import { Select } from 'antd';
import Input from 'antd/es/input/Input';
import { questionTypes, thinkingLevels } from 'common/data';
import { BaseIdRequest, ExamPartQuestionImportRequest } from 'model/request';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useExamEditPractice, useExerciseFormatsSelector, useMasterSkillTypesSelector } from '../../../redux/selector';
import { updateExamPartQuestionRequest } from '../../../redux/slice/examEditSlice';
import { AppDispatch } from 'redux/store/store';
import { QuestionType } from 'common/enum';
import CkEditorWord4 from 'component/common/CkEditorWord4/CkEditorWord4';
import { cloneDeep } from 'lodash';
import { updateLoading } from '../../../redux/slice/appConfigSlice';
import { CK_EDITOR_BASE_CONFIG } from '../../../common/constant';
import EditOneChoiceQuestion from '../../common/EditOneChoiceQuestion';
import EditYesNoQuestion from '../../common/EditYesNoQuestion';
import EditShortQuestion from '../../common/EditShortQuestion';

type Props = {
  examPartQuestion: ExamPartQuestionImportRequest | null | undefined;
  subject: BaseIdRequest | undefined;
};

const config = {
  ...CK_EDITOR_BASE_CONFIG,
  height: 200,
  extraPlugins: 'niw_btn,ckeditor_wiris',
  allowedContent: true,
  font_defaultLabel: 'Arial',
  fontSize_defaultLabel: 16,
  contentsCss: ["body {font-size: 18px; font-family: 'Arial';word-wrap:break-word;}"],
  actionFocus: false,
};

const EditQuestionView = forwardRef(({ examPartQuestion, subject }: Props) => {
  const [examPartQuestionEdit, setExamPartQuestionEdit] = useState<ExamPartQuestionImportRequest | null | undefined>(
    examPartQuestion,
  );

  const examPartQuestionEditRef = useRef<ExamPartQuestionImportRequest | null | undefined>();
  const exerciseFormats = useExerciseFormatsSelector();
  const skillTypes = useMasterSkillTypesSelector();
  const dispatch = useDispatch<AppDispatch>();
  const editView = useRef(null);
  const practice = useExamEditPractice();

  const saveChanges = function (e: any) {
    e.preventDefault();
  };

  const onChangeInput = function (e: any, fieldName: string) {
    if (!examPartQuestionEdit) return;
    const examPartQuestionEditTemp = cloneDeep(examPartQuestionEdit);
    switch (fieldName) {
      case 'mark':
      case 'weight':
        examPartQuestionEditTemp[fieldName] = e.target.value;
        break;
      default:
        let questionTemp = cloneDeep(examPartQuestionEditTemp.question) as any;
        questionTemp[fieldName] = e.target.value;
        examPartQuestionEditTemp.question = questionTemp;
        break;
    }
    dispatch(updateExamPartQuestionRequest(examPartQuestionEditTemp));
  };

  const calculateEditView = function () {
    const editViewElement = (editView as any).current;
    const maxHeight = editViewElement.getBoundingClientRect().height;
    const divFirstChild = editViewElement.firstChild;
    divFirstChild.style.height = maxHeight - 5 + 'px';
    divFirstChild.classList.remove('hidden');
  };

  const scrollToTop = function () {
    const editViewElement = (editView as any).current;
    const divFirstChild = editViewElement.firstChild;
    divFirstChild.scrollTop = 0;
  };

  const onChangeSelect = function (e: any, fieldName: string) {
    const examPartQuestionEditTemp = { ...examPartQuestionEdit } as any;

    switch (fieldName) {
      case 'exerciseFormats':
      case 'skillTypes':
        examPartQuestionEditTemp.question = {
          ...examPartQuestionEditTemp.question,
          [fieldName]: e.map((item: any) => ({
            id: item,
          })),
        };
        break;
      default:
        examPartQuestionEditTemp.question = { ...examPartQuestionEditTemp.question, [fieldName]: e };
        break;
    }

    dispatch(updateExamPartQuestionRequest(examPartQuestionEditTemp));
  };

  const onChangeCkeditor = (content: string, filedName: string) => {
    if (!examPartQuestionEditRef.current) return;
    const examPartQuestionEditTemp = { ...examPartQuestionEditRef.current } as any;
    examPartQuestionEditTemp.question = { ...examPartQuestionEditRef.current.question, [filedName]: content };

    dispatch(updateExamPartQuestionRequest(examPartQuestionEditTemp));
  };

  const handleChangeQuestionDetails = useCallback(
    (data: any, rightAnswer: string | number | undefined | null, questionType: QuestionType) => {
      const examPartQuestionEditTemp = { ...examPartQuestionEditRef.current } as ExamPartQuestionImportRequest;
      switch (questionType) {
        case QuestionType.ONE_CHOICE_QUESTION:
          if (data) {
            examPartQuestionEditTemp.question = { ...examPartQuestionEditTemp.question, questionDetails: data };
          } else if (rightAnswer !== null && rightAnswer !== undefined) {
            examPartQuestionEditTemp.question = { ...examPartQuestionEditTemp.question, rightAnswer: rightAnswer + '' };
          }
          break;
        case QuestionType.YES_NO_QUESTION:
        case QuestionType.SHORT_QUESTION:
          examPartQuestionEditTemp.question = { ...examPartQuestionEditTemp.question, rightAnswer: rightAnswer + '' };
          break;
        default:
          break;
      }

      dispatch(updateExamPartQuestionRequest(examPartQuestionEditTemp));
    },
    [dispatch],
  );

  const editQuestionDetails = useMemo(() => {
    switch (examPartQuestionEdit?.question.type) {
      case QuestionType.ONE_CHOICE_QUESTION:
        return (
          <EditOneChoiceQuestion
            questionDetails={examPartQuestionEdit.question.questionDetails}
            commitChange={handleChangeQuestionDetails}
            rightAnswer={examPartQuestionEdit.question.rightAnswer}
          />
        );
      case QuestionType.YES_NO_QUESTION:
        return (
          <EditYesNoQuestion
            commitChange={handleChangeQuestionDetails}
            rightAnswer={examPartQuestionEdit.question.rightAnswer}
          />
        );
      case QuestionType.SHORT_QUESTION:
      case QuestionType.TOPIC_QUESTION:
        return (
          <EditShortQuestion
            commitChange={handleChangeQuestionDetails}
            rightAnswer={examPartQuestionEdit.question.rightAnswer}
          />
        );
    }
    return null;
  }, [
    examPartQuestionEdit?.question.questionDetails,
    examPartQuestionEdit?.question.rightAnswer,
    examPartQuestionEdit?.question.type,
    handleChangeQuestionDetails,
  ]);

  const getSkillType = useMemo(() => {
    return examPartQuestion?.question.skillTypes ? examPartQuestion?.question.skillTypes.map((item) => item.id) : [];
  }, [examPartQuestion?.question.skillTypes]);

  const getExerciseFormat = useMemo(() => {
    return examPartQuestion?.question.exerciseFormats
      ? examPartQuestion?.question.exerciseFormats.map((item) => item.id)
      : [];
  }, [examPartQuestion?.question.exerciseFormats]);

  const getOptionSkillTypes = useMemo(() => {
    return skillTypes
      .filter((item) => {
        if (subject) {
          return item.subject.id === subject.id;
        } else return true;
      })
      .map((item) => ({
        value: item.id,
        label: `${item.code}-${item.name}`,
      }));
  }, [skillTypes, subject]);

  const getOptionExerciseFormats = useMemo(() => {
    return exerciseFormats
      .filter((item) => {
        if (subject) {
          return item.subject.id === subject.id;
        } else return true;
      })
      .map((item) => ({
        value: item.id,
        label: `${item.code}-${item.name}`,
      }));
  }, [exerciseFormats, subject]);

  useEffect(() => {
    calculateEditView();
    setExamPartQuestionEdit(examPartQuestion);
    examPartQuestionEditRef.current = examPartQuestion;
  }, [examPartQuestion]);

  useEffect(() => {
    dispatch(updateLoading(true));

    setTimeout(() => {
      dispatch(updateLoading(false));
      scrollToTop();
    }, 1000);
  }, [examPartQuestion?.id]);

  return (
    <form className="w-full h-full p-4 flex flex-col" onSubmit={saveChanges} ref={editView}>
      <div className="overflow-auto hidden pb-8">
        <div
          className={
            'flex items-center mt-2 ' +
            (examPartQuestionEdit?.question.type === QuestionType.GROUPED_QUESTION ? 'hidden' : '')
          }
        >
          <div className="text-[18px] w-[20%] text-left">Trang số:</div>
          <Input
            placeholder="Trang số"
            type="number"
            name="point"
            className=" w-fit"
            value={examPartQuestionEdit?.question.pageNumber}
            onChange={(e) => onChangeInput(e, 'pageNumber')}
          />
        </div>
        {!practice ? (
          <div
            className={
              'flex items-center mt-2 ' +
              (examPartQuestionEdit?.question.type === QuestionType.GROUPED_QUESTION ||
              examPartQuestionEdit?.question.type === QuestionType.YES_NO_QUESTION
                ? 'hidden'
                : '')
            }
          >
            <div className="text-[18px] w-[20%] text-left">Điểm:</div>
            <Input
              placeholder="Nhập điểm"
              type="number"
              name="point"
              className=" w-fit"
              value={examPartQuestion?.mark}
              onChange={(e) => onChangeInput(e, 'mark')}
            />
          </div>
        ) : null}
        {!practice ? (
          <div
            className={
              'flex items-center mt-2 ' +
              (examPartQuestionEdit?.question.type === QuestionType.GROUPED_QUESTION ||
              examPartQuestionEdit?.question.type === QuestionType.YES_NO_QUESTION
                ? 'hidden'
                : '')
            }
          >
            <div className="text-[18px] w-[20%] text-left">Trọng số:</div>
            <Input
              placeholder="Trọng số"
              type="number"
              name="point"
              className=" w-fit"
              value={examPartQuestion?.weight}
              onChange={(e) => onChangeInput(e, 'weight')}
            />
          </div>
        ) : null}
        <div className={'flex items-center mt-2'}>
          <div className="text-[18px] w-[20%] text-left">Mã câu hỏi:</div>
          <Input
            placeholder="Mã câu hỏi"
            name="point"
            className=" w-fit"
            value={examPartQuestion?.question.code}
            onChange={(e) => onChangeInput(e, 'code')}
          />
        </div>
        <div className="items-center mt-2">
          <div className="text-[18px] w-[20%] text-left">Đề bài:</div>
          <div className="mt-4">
            <CkEditorWord4
              config={config}
              value={examPartQuestion?.question?.topic ?? ''}
              keyId={examPartQuestion?.id}
              onChangeEditor={(content) => {
                onChangeCkeditor(content, 'topic');
              }}
            />
          </div>
        </div>
        <div className="flex items-center mt-2">
          <div className="text-[18px] w-[20%] text-left">Chọn loại câu hỏi:</div>
          <div className=" min-w-[300px] max-w-[300px]">
            <Select
              onChange={(e) => onChangeSelect(e, 'type')}
              className="w-full"
              placeholder="Loại câu hỏi"
              style={{ flex: 1 }}
              disabled={examPartQuestionEdit?.question.type === QuestionType.GROUPED_QUESTION}
              value={examPartQuestionEdit ? examPartQuestionEdit.question.type : null}
              options={questionTypes.map((ques) => ({
                value: ques.key,
                label: ques.value,
              }))}
            />
          </div>
        </div>
        <div
          className={
            'flex items-center mt-2 ' +
            (examPartQuestionEdit?.question.type === QuestionType.GROUPED_QUESTION ? 'hidden' : '')
          }
        >
          <div className="text-[18px] w-[20%] text-left">Dạng bài tập:</div>
          <div className=" min-w-[300px] max-w-[300px]">
            <Select
              mode="multiple"
              onChange={(e) => onChangeSelect(e, 'exerciseFormats')}
              className="w-full"
              placeholder="Dạng bài tập"
              style={{ flex: 1 }}
              value={getExerciseFormat}
              options={getOptionExerciseFormats}
            />
          </div>
        </div>
        <div
          className={
            'flex items-center mt-2 ' +
            (examPartQuestionEdit?.question.type === QuestionType.GROUPED_QUESTION ? 'hidden' : '')
          }
        >
          <div className="text-[18px] w-[20%] text-left">Năng lực:</div>
          <div className=" min-w-[300px] max-w-[300px]">
            <Select
              mode="multiple"
              onChange={(e) => onChangeSelect(e, 'skillTypes')}
              className="w-full"
              placeholder="Năng lực"
              style={{ flex: 1 }}
              value={getSkillType}
              options={getOptionSkillTypes}
            />
          </div>
        </div>
        <div
          className={
            'flex items-center mt-2 ' +
            (examPartQuestionEdit?.question.type === QuestionType.GROUPED_QUESTION ? 'hidden' : '')
          }
        >
          <div className="text-[18px] w-[20%] text-left">Mức độ:</div>
          <div className=" min-w-[300px] max-w-[300px]">
            <Select
              mode="multiple"
              onChange={(e) => onChangeSelect(e, 'thinkingLevels')}
              className="w-full"
              placeholder="Mức độ"
              style={{ flex: 1 }}
              value={examPartQuestionEdit?.question.thinkingLevels}
              options={thinkingLevels.map((thinkLevel) => ({
                value: thinkLevel.key,
                label: `${thinkLevel.code}-${thinkLevel.value}`,
              }))}
            />
          </div>
        </div>
        <div
          className={
            'items-center mt-2 w-full ' +
            (examPartQuestionEdit?.question.type === QuestionType.GROUPED_QUESTION ? 'hidden' : '')
          }
        >
          <div className="text-[18px] w-[20%] text-left">Câu trả lời:</div>
          {editQuestionDetails}
        </div>
        <div
          className={
            'items-center mt-2 ' +
            (examPartQuestionEdit?.question.type === QuestionType.GROUPED_QUESTION ? 'hidden' : '')
          }
        >
          <div className="text-[18px] w-[20%] text-left">Gợi ý, hướng dẫn giải:</div>
          <div className="mt-4">
            <CkEditorWord4
              config={config}
              value={examPartQuestionEdit?.question?.hint ?? ''}
              onChangeEditor={(content) => {
                onChangeCkeditor(content, 'hint');
              }}
              keyId={examPartQuestionEdit?.id}
            />
          </div>
        </div>
        <div className="items-center mt-2">
          <div className="text-[18px] w-[20%] text-left">Lời giải chi tiết:</div>
          <div className="mt-4">
            <CkEditorWord4
              config={config}
              value={examPartQuestionEdit?.question?.solutionGuide ?? ''}
              onChangeEditor={(content) => {
                onChangeCkeditor(content, 'solutionGuide');
              }}
              keyId={examPartQuestionEdit?.id}
            />
          </div>
        </div>
      </div>
    </form>
  );
});

export default EditQuestionView;
